import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "unlayer-editor-wrapper h-full" };
import { ref, computed, onBeforeUnmount, watch } from "vue";
import { useI18n } from "vue-i18n";
import { EmailEditor } from "vue-email-editor";
import { configService } from "@/infrastructure/services/ConfigService";
import { logger } from "@/infrastructure/services/LoggerService";
import { filesApi } from "../../infrastructure/api/files-api";
import { getApiErrorStatus } from "@/shared/utils/apiErrorStatus";
import { checkEditorImage, DEFAULT_EDITOR_IMAGE_MAX_BYTES, } from "../../domain/editorImage";
import { PREFERENCE_CENTER_MERGE_TOKEN, WEB_VERSION_MERGE_TOKEN, } from "../../domain/mergeFieldTokens";
const INITIAL_LOAD_TIMEOUT_MS = 5000;
const SETTLE_AFTER_LOAD_MS = 600;
const saveShortcutJs = `document.addEventListener("keydown",function(e){if((e.ctrlKey||e.metaKey)&&e.key==="s"){e.preventDefault();window.parent.postMessage({type:"unlayer:save-shortcut"},"*")}});`;
export default /*@__PURE__*/ _defineComponent({
    __name: 'UnlayerEditor',
    props: {
        templateId: {},
        initialDesign: { default: undefined },
        minHeight: { default: "100%" },
        mergeTags: { default: undefined },
        validator: { type: Function, default: undefined }
    },
    emits: ["design-updated", "editor-ready", "save-shortcut"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { locale, t } = useI18n();
        const emailEditorRef = ref(null);
        /**
         * OP-6346: Unlayer emits `design:updated` during its OWN initial render — both when we load a
         * provided design and when it boots the blank default design for a brand-new template. Those
         * programmatic updates must NOT be reported as user edits, otherwise a freshly created template
         * is flagged dirty before the user touches anything (Save enabled, spurious unsaved-changes
         * modal on Retour, and — if the user leaves via "Quitter" — an unpublishable template).
         *
         * Observed init sequence: `design:loaded` fires first, then Unlayer's init `design:updated`
         * follows a few ms later. So we keep the gate CLOSED until `design:loaded` AND for a short settle
         * window after it, which swallows that trailing programmatic burst. Genuine user edits arrive far
         * later, once the gate is open. Event-driven (no pre-export sleep, per R54) with a timeout
         * fallback so a silent Unlayer that never emits `design:loaded` cannot permanently disable Save.
         */
        const isReady = ref(false);
        let readyTimer = null;
        let settleTimer = null;
        function clearReadyTimers() {
            if (readyTimer) {
                clearTimeout(readyTimer);
                readyTimer = null;
            }
            if (settleTimer) {
                clearTimeout(settleTimer);
                settleTimer = null;
            }
        }
        function markReady() {
            if (isReady.value)
                return;
            isReady.value = true;
            clearReadyTimers();
            emailEditorRef.value?.editor?.removeEventListener("design:loaded", onInitialDesignLoaded);
        }
        function onInitialDesignLoaded() {
            // design:loaded fired — the init design:updated burst follows within a few ms. Defer opening
            // the gate by a short settle window so those programmatic updates stay suppressed.
            if (isReady.value || settleTimer)
                return;
            if (readyTimer) {
                clearTimeout(readyTimer);
                readyTimer = null;
            }
            settleTimer = setTimeout(markReady, SETTLE_AFTER_LOAD_MS);
        }
        const unlayerProjectIdStr = configService.api?.unlayerProjectId;
        const projectId = unlayerProjectIdStr ? Number(unlayerProjectIdStr) : undefined;
        const currentLocale = computed(() => locale.value);
        /** Inline script injected into the Unlayer iframe to forward Ctrl+S to the parent */
        const editorOptions = computed(() => ({
            displayMode: "email",
            features: {
                preheaderText: false,
                textEditor: {
                    spellChecker: true,
                },
                headersAndFooters: true,
                // OP-5630: the Audit tab is only useful once something populates it.
                audit: props.validator !== undefined,
            },
            editor: {
                minRows: 1,
                minFooters: 1,
                maxFooters: 1,
            },
            customJS: [`data:text/javascript,${encodeURIComponent(saveShortcutJs)}`],
            // OP-6424/OP-7732: expose the preference-center and "view in browser" links as Unlayer "special
            // links" so they can be applied to a button OR a text selection (not just dropped as a raw
            // merge tag). The hrefs stay the raw `{{preference_center_url}}` / `{{web_version_url}}` tokens —
            // OPINK substitutes a per-recipient URL at send time (the preference center additionally
            // validates the HTML contains its literal token via the MISSING_PREFERENCES_LINK guard). The
            // matching merge tags are removed from the merge-tag dropdown in useMergeFields (dedup).
            specialLinks: [
                {
                    name: t("templating.specialLinks.preferenceCenter"),
                    href: PREFERENCE_CENTER_MERGE_TOKEN,
                    target: "_blank",
                },
                {
                    name: t("templating.specialLinks.viewInBrowser"),
                    href: WEB_VERSION_MERGE_TOKEN,
                    target: "_blank",
                },
            ],
            ...(props.mergeTags ? { mergeTags: props.mergeTags } : {}),
        }));
        const appearance = {
            theme: "light",
        };
        function onEditorLoad() {
            // OP-6346: arm the initial-render guard BEFORE loading any design. design:loaded opens a short
            // settle window (onInitialDesignLoaded) after which isReady flips; until then every
            // design:updated is treated as Unlayer's own init noise and dropped by onDesignUpdated.
            emailEditorRef.value?.editor?.addEventListener("design:loaded", onInitialDesignLoaded);
            readyTimer = setTimeout(markReady, INITIAL_LOAD_TIMEOUT_MS);
            // Load initial design if provided
            if (props.initialDesign) {
                try {
                    const design = JSON.parse(props.initialDesign);
                    emailEditorRef.value?.editor?.loadDesign(design);
                }
                catch {
                    // Invalid JSON design — start with blank editor
                }
            }
            // Register design:updated listener for auto-save tracking
            emailEditorRef.value?.editor?.addEventListener("design:updated", onDesignUpdated);
            // Set merge tags if provided
            if (props.mergeTags) {
                emailEditorRef.value?.editor?.setMergeTags(props.mergeTags);
            }
            // Registered once: the validator closes over reactive refs, so it re-reads the current email
            // type and merge catalogue on every audit run.
            if (props.validator) {
                emailEditorRef.value?.editor?.setValidator(props.validator);
            }
            // Register custom file storage callback for editor image uploads (drag & drop)
            // Thumbnail uploads are handled separately by exportImage() → fetch → upload
            emailEditorRef.value?.editor?.registerCallback("image", uploadEditorImage);
            emit("editor-ready");
        }
        const maxImageBytes = configService.api?.editorImageMaxBytes ?? DEFAULT_EDITOR_IMAGE_MAX_BYTES;
        function imageRejectionMessage(rejection) {
            return rejection === "tooLarge"
                ? t("templating.editor.imageUpload.tooLarge", { size: Math.floor(maxImageBytes / 1024) })
                : t("templating.editor.imageUpload.invalidType");
        }
        // CONSTRAINT: Unlayer ends an upload only on `url`, `error` or `abort`. Any other `done()` payload,
        // `{ progress: 0 }` included, is a progress update and leaves its spinner turning (OP-7964).
        async function uploadEditorImage(file, done) {
            const image = file.attachments[0];
            const rejection = checkEditorImage(image, maxImageBytes);
            if (rejection) {
                done({ error: imageRejectionMessage(rejection) });
                return;
            }
            try {
                const formData = new FormData();
                formData.append("file", image);
                const result = await filesApi.uploadFile(formData, "EditorImage");
                done({ progress: 100, url: result.url });
            }
            catch (err) {
                const status = getApiErrorStatus(err);
                logger.error("[UnlayerEditor] Editor image upload failed", { status });
                done({
                    error: status === 413
                        ? imageRejectionMessage("tooLarge")
                        : t("templating.editor.imageUpload.failed"),
                });
            }
        }
        watch(() => props.mergeTags, (tags) => {
            if (tags) {
                emailEditorRef.value?.editor?.setMergeTags(tags);
            }
        });
        function onMessage(event) {
            if (event.data?.type === "unlayer:save-shortcut") {
                emit("save-shortcut");
            }
        }
        window.addEventListener("message", onMessage);
        function onDesignUpdated() {
            // OP-6346: drop Unlayer's initial-render updates (before design:loaded + settle window); only
            // genuine user edits after the design has settled mark the template dirty.
            if (!isReady.value)
                return;
            emit("design-updated");
        }
        /**
         * Exports both the Unlayer design JSON and compiled HTML.
         * Returns a Promise for easy async/await usage.
         */
        function getDesignAndHtml() {
            return new Promise((resolve, reject) => {
                const editor = emailEditorRef.value?.editor;
                if (!editor) {
                    reject(new Error("Editor not initialized"));
                    return;
                }
                editor.exportHtml((data) => {
                    resolve({ design: data.design, html: data.html });
                });
            });
        }
        /**
         * Exports the current design as a PNG thumbnail via Unlayer Cloud API,
         * then downloads the image and re-uploads it to our MinIO storage.
         *
         * exportImage() returns an Unlayer CDN URL — it does NOT trigger registerCallback('image').
         * We must fetch the image ourselves and upload it to our backend.
         *
         * Unlayer's exportImage callback is NOT called on error (e.g., 422 from their Cloud API),
         * so we use a timeout to avoid hanging indefinitely.
         */
        /**
         * Exports the current design as a PNG thumbnail and uploads it to MinIO under the file identifier
         * the caller provides (object key: `thumbnails/{fileIdentifier}.png`). The caller decides the
         * layout — Templates pass `{templateId}-{versionId}` (per-version), ActionContents pass their own
         * id. Returns `true` when the upload succeeded so callers can flag the new persisted entity as
         * having a thumbnail; returns `false` on Unlayer/CDN/upload failure (non-blocking — the save
         * continues without a thumbnail).
         */
        async function exportImage(fileIdentifier) {
            const editor = emailEditorRef.value?.editor;
            if (!editor) {
                return false;
            }
            const EXPORT_TIMEOUT_MS = 15000;
            const unlayerUrl = await new Promise((resolve) => {
                const timer = setTimeout(() => {
                    logger.error("[Thumbnail] Unlayer exportImage timed out — Cloud API likely failed. Save will continue without thumbnail.");
                    resolve(null);
                }, EXPORT_TIMEOUT_MS);
                editor.exportImage((data) => {
                    clearTimeout(timer);
                    resolve(data.url ?? null);
                });
            });
            if (!unlayerUrl) {
                return false;
            }
            const response = await fetch(unlayerUrl);
            if (!response.ok) {
                logger.error("Failed to fetch thumbnail from Unlayer CDN", { status: response.status });
                return false;
            }
            const blob = await response.blob();
            const identifier = fileIdentifier ?? props.templateId;
            const formData = new FormData();
            formData.append("file", blob, `${identifier}.png`);
            try {
                await filesApi.uploadFile(formData, "TemplateThumbnail", identifier);
                return true;
            }
            catch (err) {
                logger.error("Thumbnail upload failed", err);
                return false;
            }
        }
        onBeforeUnmount(() => {
            window.removeEventListener("message", onMessage);
            emailEditorRef.value?.editor?.removeEventListener("design:updated", onDesignUpdated);
            emailEditorRef.value?.editor?.removeEventListener("design:loaded", onInitialDesignLoaded);
            emailEditorRef.value?.editor?.clearValidators();
            clearReadyTimers();
        });
        /**
         * Loads a new design into the editor and resolves once Unlayer signals the render is done
         * (`design:loaded`). Awaiting the returned Promise lets callers run `exportImage` against the
         * restored design without racing the asynchronous re-render. A 5 s timeout fallback resolves the
         * Promise even if the event never fires (Unlayer is silent on parse errors).
         */
        function loadDesign(designJson) {
            let design;
            try {
                design = JSON.parse(designJson);
            }
            catch {
                return Promise.resolve();
            }
            const editor = emailEditorRef.value?.editor;
            if (!editor) {
                return Promise.resolve();
            }
            const LOAD_TIMEOUT_MS = 5000;
            return new Promise((resolve) => {
                let settled = false;
                const onLoaded = () => {
                    if (settled)
                        return;
                    settled = true;
                    editor.removeEventListener("design:loaded", onLoaded);
                    clearTimeout(timer);
                    resolve();
                };
                const timer = setTimeout(() => {
                    if (settled)
                        return;
                    settled = true;
                    editor.removeEventListener("design:loaded", onLoaded);
                    logger.error("[UnlayerEditor] design:loaded did not fire within timeout — proceeding without re-render guarantee");
                    resolve();
                }, LOAD_TIMEOUT_MS);
                editor.addEventListener("design:loaded", onLoaded);
                editor.loadDesign(design);
            });
        }
        // Expose methods to parent
        __expose({
            getDesignAndHtml,
            exportImage,
            loadDesign,
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(EmailEditor), {
                    ref_key: "emailEditorRef",
                    ref: emailEditorRef,
                    "project-id": _unref(projectId),
                    "min-height": __props.minHeight,
                    options: editorOptions.value,
                    appearance: appearance,
                    locale: currentLocale.value,
                    onLoad: onEditorLoad
                }, null, 8 /* PROPS */, ["project-id", "min-height", "options", "locale"])
            ]));
        };
    }
});
